import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStrings } from '../locales';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { toPng } from 'html-to-image';
import { ancestries as ancestriesData, Ancestry } from '../character/content/ancestries';
import { fuzzyFilter } from '../search/fuzzySearch';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import { useContentVersion } from '../hooks/ContentVersionContext';
import { pickVersion, matchesSource, SOURCE_OPTIONS } from '../character/content/sources';
import { SwipeHint } from '../components/CardSheet';
import '../components/CardSheet.css';
import { AncestrySheet } from '../character/cards/AncestrySheet';
import './Ancestries.css';


// ── Mixed Ancestry collapsible ────────────────────────────────────────
interface MixedAncestryRule { label: string; text: string; }

function MixedAncestrySection() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mixed-ancestry">
      <button className="mixed-ancestry__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="mixed-ancestry__title">Mixed Ancestry</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          className={`mixed-ancestry__chevron${open ? ' mixed-ancestry__chevron--open' : ''}`} aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="mixed-ancestry__body">
          <p className="mixed-ancestry__intro">
            Some characters are born from two different ancestries. If you want your character to reflect a mixed heritage, you may choose a Mixed Ancestry during character creation.
          </p>
          <div className="mixed-ancestry__grid">
            {([
              { label: 'Choose Two Ancestries', text: "Select two ancestries that represent your character's lineage. These can be any two ancestries available in your game." },
              { label: 'One Feature Each', text: 'Instead of taking both features from a single ancestry, take the first feature from the first ancestry and the second feature from the second ancestry.' },
              { label: 'Physicality', text: "Work with your GM to describe what your mixed heritage looks like. Your character's appearance may blend traits from both ancestries in any way you choose." },
            ] as MixedAncestryRule[]).map(({ label, text }) => (
              <div key={label} className="mixed-ancestry__rule">
                <div className="mixed-ancestry__rule-label">{label}</div>
                <div className="mixed-ancestry__rule-text">{text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────
function AncestryModal({ items, idx, onClose, onNav }: {
  items: Ancestry[];
  idx: number;
  onClose: () => void;
  onNav: (i: number) => void;
}) {
  const ancestry = items[idx];
  const cardRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);
  const [flipped, setFlipped] = useState(false);

  const prev = useCallback(() => onNav((idx - 1 + items.length) % items.length), [idx, items.length, onNav]);
  const next = useCallback(() => onNav((idx + 1) % items.length), [idx, items.length, onNav]);

  useEffect(() => { setFlipped(false); }, [idx]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    if (cardRef.current) cardRef.current.style.transition = 'none';
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    cardRef.current.style.transform = `translateX(${Math.max(-60, Math.min(60, dx))}px)`;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current || !cardRef.current) return;
    isDragging.current = false;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    cardRef.current.style.transition = '';
    cardRef.current.style.transform = '';
    if (dx < -60) next();
    else if (dx > 60) prev();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const handleDownload = useCallback(async () => {
    const wrapper = cardRef.current;
    if (!wrapper) return;
    const slug = ancestry.name.replace(/\s+/g, '-').toLowerCase();

    const captureFace = async (face: HTMLElement, page: string) => {
      const savedBfv = face.style.backfaceVisibility;
      const savedTransform = face.style.transform;
      face.style.backfaceVisibility = 'visible';
      face.style.transform = 'none';
      try {
        const url = await toPng(face, { pixelRatio: Math.max(window.devicePixelRatio, 2) });
        const a = document.createElement('a');
        a.download = `${slug}-ancestry-${page}.png`;
        a.href = url; a.click();
      } finally {
        face.style.backfaceVisibility = savedBfv;
        face.style.transform = savedTransform;
      }
    };

    const front = wrapper.querySelector<HTMLElement>('.portrait-card__front');
    const back = wrapper.querySelector<HTMLElement>('.portrait-card__back');
    if (front) await captureFace(front, '1-2');
    if (back) await captureFace(back, '2-2');
    if (!front && !back) {
      const card = wrapper.querySelector<HTMLElement>('.portrait-card') ?? wrapper;
      const url = await toPng(card, { pixelRatio: Math.max(window.devicePixelRatio, 2) });
      const a = document.createElement('a');
      a.download = `${slug}-ancestry.png`;
      a.href = url; a.click();
    }
  }, [ancestry, cardRef]);

  return (
    <div className="card-modal-overlay" onClick={onClose}>
      <button className="card-nav-btn card-nav-btn--prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous">
        <ChevronLeft size={22} />
      </button>
      <SwipeHint />
      <div ref={cardRef} className="card-modal-card-wrapper" onClick={(e) => e.stopPropagation()} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <AncestrySheet key={ancestry.name} ancestry={ancestry} preview={false} flipped={flipped} onFlip={() => setFlipped(f => !f)} onClose={onClose} />
      </div>
      <button className="card-download-btn" onClick={(e) => { e.stopPropagation(); handleDownload(); }} aria-label="Download card" title="Download card">
        <Download size={16} /> Download
      </button>
      <button className="card-nav-btn card-nav-btn--next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next">
        <ChevronRight size={22} />
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function Ancestries() {
  const s = useStrings().ancestries;
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get('q') || '');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
  }, [searchParams]);
  const [sourceFilter, setSource] = useState('All');
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const { glows, onSourceClick } = useNewSourceFilter('/ancestries');
  const [version] = useContentVersion();

  const fuzzy = fuzzyFilter(
    pickVersion(ancestriesData, version),
    search,
    (a) => [a.description?.join(' ') || '', ...(a.features || []).map((f) => f.name)].join(' '),
    (a) => a.name
  );

  const filtered = fuzzy.filter((a) => matchesSource(sourceFilter, a.source));

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <div className="filter-bar" style={{ marginBottom: '20px' }}>
        <input
          type="text"
          id="ancestries-search"
          name="ancestries-search"
          placeholder={s.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ancestry-search"
          aria-label={s.searchAria}
        />
        {SOURCE_OPTIONS.map((opt) => (
          <button key={opt}
            className={`filter-btn${sourceFilter === opt ? ' active' : ''}${glows(opt) ? ' new-source-glow' : ''}`}
            onClick={() => { setSource(opt); onSourceClick(opt); }}
          >
            {opt}
          </button>
        ))}
      </div>

      <div className="card-grid">
        {filtered.map((a, i) => (
          <button key={a.name} className={`card-btn${search && i === 0 ? ' card-btn--highlight' : ''}`} onClick={() => setOpenIdx(i)} aria-label={`Open ${a.name}`}>
            <AncestrySheet ancestry={a} preview />
          </button>
        ))}
      </div>

      {openIdx !== null && (
        <AncestryModal items={filtered} idx={openIdx} onClose={() => setOpenIdx(null)} onNav={setOpenIdx} />
      )}

      <div className="divider" />
      <MixedAncestrySection />
    </div>
  );
}
